import React, { useState } from 'react';
import styled from 'styled-components';
import { FiMail, FiCalendar, FiEye } from 'react-icons/fi';
import { GiCoral, GiTropicalFish } from 'react-icons/gi';
import { Card, Button, Badge } from '../styles/GlobalStyles';

const ItemCardContainer = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props => props.theme.shadows.ocean};
  }
`;

const ItemImage = styled.div`
  width: 100%;
  height: 200px;
  background: ${props => props.image ? 
    `url(${props.image}) center/cover` : 
    props.theme.gradients.wave};
  border-radius: ${props => props.theme.borderRadius.medium};
  margin-bottom: 1.5rem;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => !props.image ? 
      'rgba(0, 105, 148, 0.1)' : 
      'linear-gradient(45deg, transparent 0%, rgba(0, 0, 0, 0.1) 100%)'};
  }
`;

const PlaceholderIcon = styled.div`
  font-size: 4rem;
  color: white;
  z-index: 2;
  opacity: 0.8;
`;

const ItemHeader = styled.div`
  margin-bottom: 1rem;
`;

const ItemName = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  line-height: 1.3;
`;

const ScientificName = styled.p`
  font-style: italic;
  color: ${props => props.theme.colors.textLight};
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const ItemDescription = styled.p.withConfig({
  shouldForwardProp: (prop) => !['expanded'].includes(prop),
})`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: 1rem;
  ${props => !props.expanded && `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
  flex-grow: 1;
`;

const ShowMoreButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  text-decoration: underline;
  
  &:hover {
    color: ${props => props.theme.colors.secondary};
  }
`;

const ItemDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  line-height: 1;
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textLight};
  margin-bottom: 1rem;
`;

const ItemPrice = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 1rem;
`;

const ItemActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: auto;
`;

const getDifficultyColor = (level) => {
  switch(level) {
    case 'Anfänger': return 'emerald';  // Beginner - emerald
    case 'Mittel': return 'amber';  // Intermediate - amber
    case 'Fortgeschritten': return 'error';  // Advanced - error (red)
    default: return 'info';
  }
};

const getTemperamentColor = (temperament) => {
  switch(temperament) {
    case 'Friedlich': return 'green';  // Peaceful - green
    case 'Semi-Aggressiv': return 'orange';  // Semi-aggressive - orange
    case 'Aggressiv': return 'red';  // Aggressive - red
    default: return 'blue';
  }
};

const getSizeColor = (size) => {
  if (!size) return 'neutral';
  const sizeStr = size.toLowerCase();
  if (sizeStr.includes('klein') || sizeStr.includes('small') || sizeStr.includes('s')) return 'cyan';  // Small - cyan
  if (sizeStr.includes('mittel') || sizeStr.includes('medium') || sizeStr.includes('m')) return 'sky';  // Medium - sky blue
  if (sizeStr.includes('groß') || sizeStr.includes('large') || sizeStr.includes('l')) return 'violet';  // Large - violet
  if (sizeStr.includes('xl') || sizeStr.includes('sehr')) return 'fuchsia';  // XL - fuchsia
  return 'ocean';
};

const getDietColor = (diet) => {
  if (!diet) return 'neutral';
  const dietStr = diet.toLowerCase();
  if (dietStr.includes('fleisch') || dietStr.includes('carnivore')) return 'rose';  // Carnivore - rose/red
  if (dietStr.includes('pflanz') || dietStr.includes('herbivore')) return 'lime';  // Herbivore - lime green
  if (dietStr.includes('alles') || dietStr.includes('omnivore')) return 'yellow';  // Omnivore - yellow
  return 'coral';
};

const getColorVariant = (colorName) => {
  if (!colorName) return 'neutral';
  const color = colorName.toLowerCase();
  if (color.includes('rot') || color.includes('red')) return 'error';
  if (color.includes('blau') || color.includes('blue')) return 'ocean';
  if (color.includes('grün') || color.includes('green')) return 'emerald';
  if (color.includes('gelb') || color.includes('yellow')) return 'amber';
  if (color.includes('lila') || color.includes('purple') || color.includes('violett')) return 'purple';
  if (color.includes('rosa') || color.includes('pink')) return 'pink';
  if (color.includes('orange')) return 'coral';
  if (color.includes('braun') || color.includes('brown')) return 'slate';
  return 'coral';
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatPrice = (price) => {
  if (!price) return null;
  // If price already contains CHF, return as is
  if (price.includes('CHF')) return price;
  // Otherwise, add CHF prefix
  return `CHF ${price}`;
};

const ItemCard = ({ item, type, onContact, onViewDetails }) => {
  const isCoralType = type === 'coral';
  const [showFullDescription, setShowFullDescription] = useState(false);
  
  const shouldShowMoreButton = item.description && item.description.length > 150;

  return (
    <ItemCardContainer>
      <ItemImage image={item.image_url}>
        {!item.image_url && (
          <PlaceholderIcon>
            {isCoralType ? <GiCoral /> : <GiTropicalFish />}
          </PlaceholderIcon>
        )}
      </ItemImage>

      <ItemHeader>
        <ItemName>{item.name}</ItemName>
        {item.scientific_name && (
          <ScientificName>{item.scientific_name}</ScientificName>
        )}
      </ItemHeader>

      <ItemDescription expanded={showFullDescription}>
        {item.description}
      </ItemDescription>
      
      {shouldShowMoreButton && (
        <ShowMoreButton 
          onClick={() => setShowFullDescription(!showFullDescription)}
        >
          {showFullDescription ? 'Weniger anzeigen' : 'Mehr anzeigen'}
        </ShowMoreButton>
      )}

      <ItemDetails>
        {/* Availability Status Badge */}
        <Badge variant={item.available ? 'success' : 'slate'} noTransform>
          {item.available ? 'Verfügbar' : 'Nicht verfügbar'}
        </Badge>
        
        {isCoralType ? (
          <>
            {item.size && <Badge variant={getSizeColor(item.size)}>{item.size}</Badge>}
            {item.color && <Badge variant={getColorVariant(item.color)}>{item.color}</Badge>}
            {item.difficulty_level && (
              <Badge variant={getDifficultyColor(item.difficulty_level)}>
                {item.difficulty_level}
              </Badge>
            )}
          </>
        ) : (
          <>
            {item.size && <Badge variant={getSizeColor(item.size)}>{item.size}</Badge>}
            {item.temperament && (
              <Badge variant={getTemperamentColor(item.temperament)}>
                {item.temperament}
              </Badge>
            )}
            {item.diet && <Badge variant={getDietColor(item.diet)}>{item.diet}</Badge>}
            {item.tank_size_min && (
              <Badge variant="ocean" noTransform>
                Min. {item.tank_size_min}L
              </Badge>
            )}
          </>
        )}
      </ItemDetails>

      {/* Price Display */}
      {item.price && (
        <ItemPrice>
          {formatPrice(item.price)}
        </ItemPrice>
      )}

      <ItemMeta>
        <FiCalendar size={16} />
        Verfügbar seit: {formatDate(item.available_since || item.created_at)}
      </ItemMeta>

      <ItemActions>
        <Button
          size="small"
          onClick={() => onViewDetails(item)}
          outline
          style={{ flex: '1' }}
        >
          <FiEye size={16} />
          Details
        </Button>
        <Button
          size="small"
          onClick={() => onContact(item)}
          style={{ flex: '1' }}
          disabled={!item.available}
          secondary={!item.available}
        >
          <FiMail size={16} />
          {item.available ? 'Interesse' : 'Nicht verfügbar'}
        </Button>
      </ItemActions>
    </ItemCardContainer>
  );
};

export default ItemCard;

